import type { Schemas } from "@norn/sdk"
import { Link } from "@tanstack/react-router"
import { ChevronRight, EyeOff } from "lucide-react"
import { useMemo, useState } from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip } from "@/components/ui/tooltip"
import { refusalOfError } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { titleCase } from "./humanize"
import { type ObjectResponse, useLinks, useReferenceIndex, useTitleOf } from "./hooks"
import type { LinkMeta, Ontology, TypeMeta } from "./ontology"
import { FieldValue, RefusalNote, RefusalSummary, StatusPill } from "./values"

type Ref = { objectType: string; key: string }

export interface RelatedGroup {
  readonly id: string
  readonly label: string
  readonly direction: "out" | "in"
  readonly otherType: string
  readonly link?: LinkMeta
  readonly items: readonly Ref[]
}

/** Group one-hop edges by meaning: links this record declares (its parents), then records that point at it. */
export function groupEdges(edges: readonly Schemas["LinkEdge"][], self: Ref, ontology: Ontology | undefined): RelatedGroup[] {
  const groups = new Map<string, { direction: "out" | "in"; otherType: string; linkId: string; items: Ref[] }>()
  for (const edge of edges) {
    const out = edge.from.key === self.key && edge.from.objectType === self.objectType
    const other = out ? edge.to : edge.from
    const id = out ? `out:${edge.linkId}` : `in:${edge.from.objectType}:${edge.linkId}`
    const g = groups.get(id) ?? { direction: out ? "out" : "in", otherType: other.objectType, linkId: edge.linkId, items: [] }
    if (!g.items.some((r) => r.key === other.key && r.objectType === other.objectType)) g.items.push({ objectType: other.objectType, key: other.key })
    groups.set(id, g)
  }
  const inTypes = new Map<string, number>()
  for (const g of groups.values()) if (g.direction === "in") inTypes.set(g.otherType, (inTypes.get(g.otherType) ?? 0) + 1)
  const result = [...groups.entries()].map(([id, g]) => {
    const other = ontology?.types.get(g.otherType)
    const source = ontology?.types.get(g.direction === "out" ? self.objectType : g.otherType)
    const link = source?.links.find((l) => l.id === g.linkId)
    const otherLabel = other ? (g.items.length === 1 ? other.label : other.pluralLabel) : titleCase(g.otherType.split(".").pop() ?? g.otherType)
    const label =
      g.direction === "out"
        ? (link?.label ?? titleCase(g.linkId))
        : (inTypes.get(g.otherType) ?? 0) > 1
          ? `${otherLabel} · ${link?.label ?? titleCase(g.linkId)}`
          : otherLabel
    return { id, label, direction: g.direction, otherType: g.otherType, link, items: g.items }
  })
  return result.sort((a, b) => (a.direction === b.direction ? a.label.localeCompare(b.label) : a.direction === "out" ? -1 : 1))
}

const PREVIEW = 3

export function RelatedPanel({ scope, self, ontology }: { scope: Scope; self: Ref; ontology: Ontology | undefined }) {
  const t = useT()
  const links = useLinks(scope, self.objectType, self.key)
  const groups = useMemo(() => groupEdges(links.data?.items ?? [], self, ontology), [links.data, self, ontology])
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const wanted = useMemo(() => groups.flatMap((g) => (expanded.has(g.id) ? g.items.slice(0, 50) : g.items.slice(0, PREVIEW))), [groups, expanded])
  const index = useReferenceIndex(scope, wanted)
  const refused = links.isError ? refusalOfError(links.error) : null
  const withheld = [...new Set(Object.values(links.data?.masked ?? {}).flatMap((m) => (m && typeof m === "object" && "markings" in m && Array.isArray(m.markings) ? (m.markings as string[]) : [])))]

  return (
    <section aria-labelledby="ws-related" className="space-y-3">
      <h2 id="ws-related" className="text-[15px] font-semibold tracking-tight">
        {t("ws.related.title")}
      </h2>
      {links.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : links.isError ? (
        <RefusalNote error={links.error} title={refused && (refused.refusal.category === "visibility" || refused.refusal.category === "permission") ? t("ws.related.hidden") : t("ws.related.failed")} />
      ) : groups.length === 0 && withheld.length ? null : groups.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border px-4 py-3.5 text-sm text-muted-foreground">{t("ws.related.none")}</p>
      ) : (
        <ul className="space-y-3">
          {groups.map((group) => {
            const open = expanded.has(group.id)
            const shown = open ? group.items.slice(0, 50) : group.items.slice(0, PREVIEW)
            const count = `${group.items.length}${links.data?.more ? "+" : ""}`
            return (
              <li key={group.id} className="rounded-2xl border border-border bg-card">
                <div className="flex items-baseline gap-2 px-4 pt-3 pb-1.5">
                  <h3 className="text-[13px] font-medium">{group.label}</h3>
                  <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
                  {group.direction === "out" ? null : <span className="sr-only">{t("ws.related.pointingHere")}</span>}
                </div>
                <ul className="pb-1.5">
                  {shown.map((ref) => (
                    <RelatedRow key={`${ref.objectType}/${ref.key}`} scope={scope} refTo={ref} object={index(ref.objectType, ref.key)} ontology={ontology} />
                  ))}
                </ul>
                {group.items.length > PREVIEW ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => (open ? new Set([...s].filter((x) => x !== group.id)) : new Set([...s, group.id])))}
                    className="w-full border-t border-border px-4 py-2 text-start text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:bg-muted/50"
                  >
                    {open ? t("ws.related.showFewer") : t("ws.related.showAll", { count: group.items.length })}
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      {withheld.length ? (
        <div className="flex items-start gap-2.5 rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          <EyeOff className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            {t("ws.related.withheld")}
            <span className="mt-0.5 block text-xs text-subtle-foreground">{withheld.join(", ")}</span>
          </p>
        </div>
      ) : null}
      <RefusalSummary errors={wanted.map((r) => index(r.objectType, r.key)).filter((r) => r.isError).map((r) => r.error)} title={t("ws.related.unreadable")} />
    </section>
  )
}

function RelatedRow({
  scope,
  refTo,
  object,
  ontology,
}: {
  scope: Scope
  refTo: Ref
  object: { data?: ObjectResponse; isPending: boolean; isError: boolean } | undefined
  ontology: Ontology | undefined
}) {
  const t = useT()
  const titleOf = useTitleOf(ontology)
  const data = object?.data
  const type = ontology?.types.get(data?.objectType ?? refTo.objectType)
  const title = titleOf(data, refTo.key)
  const refused = object?.isError
  return (
    <li>
      <Link
        to="/$orgId/$environmentId/records/$objectType/$key"
        params={{ ...scope, objectType: refTo.objectType, key: refTo.key }}
        search={{ via: "link" }}
        className={cn(
          "group mx-1.5 flex items-center gap-3 rounded-xl px-2.5 py-2 outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted/60",
          refused && "text-muted-foreground",
        )}
      >
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm">
            {object?.isPending ? (
              <Skeleton className="h-3.5 w-32" />
            ) : title.isKey ? (
              <span dir="ltr" className="truncate">
                {refTo.key}
              </span>
            ) : (
              <span className="truncate">{title.title}</span>
            )}
            {title.hidden ? (
              <Tooltip content={t("ws.titleHidden")}>
                <EyeOff className="size-3 shrink-0 text-subtle-foreground" aria-label={t("ws.titleHidden")} />
              </Tooltip>
            ) : null}
          </p>
          {data && type ? <RowFacts type={type} object={data} /> : refused ? <p className="text-xs">{t("ws.related.unreadable")}</p> : null}
        </div>
        <ChevronRight className="size-4 shrink-0 text-subtle-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 rtl:-scale-x-100" aria-hidden />
      </Link>
    </li>
  )
}

function RowFacts({ type, object }: { type: TypeMeta; object: ObjectResponse }) {
  const values = object.values as Record<string, unknown>
  const masked = object.masked as Record<string, unknown> | undefined
  const fields = [type.statusField, type.amountField, type.dateField].flatMap((id) => {
    const f = id ? type.fields.find((x) => x.id === id) : undefined
    return f ? [f] : []
  })
  if (!fields.length) return null
  return (
    <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
      {fields.map((f) => (
        <FieldValue key={f.id} field={f} values={values} masked={masked} type={type} className={f.role === "status" ? "h-5 text-2xs" : undefined} />
      ))}
    </p>
  )
}

/** A linked document's own state: its live status, or a status its type fixes (every Delivery is "delivered"). */
function progressField(type: TypeMeta) {
  return type.fields.find((f) => f.id === type.statusField) ?? type.fields.find((f) => f.role === "text" && f.options.length === 1 && /status|state/.test(f.id))
}

/**
 * Where a record stands, read from the documents that point at it (its delivery, invoice, …):
 * each shown with its own recorded status, never inferred onto this record's fields.
 */
export function LinkedProgress({ scope, self, ontology }: { scope: Scope; self: Ref; ontology: Ontology | undefined }) {
  const links = useLinks(scope, self.objectType, self.key)
  const groups = useMemo(
    () =>
      groupEdges(links.data?.items ?? [], self, ontology).filter((g) => {
        const t = ontology?.types.get(g.otherType)
        return g.direction === "in" && Boolean(t && progressField(t))
      }),
    [links.data, self, ontology],
  )
  const complete = links.data?.more === false
  // Only groups read in full: a claim like "2 invoices · Paid" must hold for every member.
  const small = useMemo(() => (complete ? groups.filter((g) => g.items.length <= 5) : []), [groups, complete])
  const refs = useMemo(() => small.flatMap((g) => g.items), [small])
  const index = useReferenceIndex(scope, refs)
  const chips = small.flatMap((g) => {
    const type = ontology?.types.get(g.otherType)
    const field = type ? progressField(type) : undefined
    if (!type || !field) return []
    const statuses = g.items.map((r) => {
      const o = index(r.objectType, r.key).data
      const masked = o?.masked as Record<string, unknown> | undefined
      const v = o ? (o.values as Record<string, unknown>)[field.id] : undefined
      return typeof v === "string" && !(masked && field.id in masked) ? v : null
    })
    if (statuses.some((v) => v === null)) return []
    const known = [...new Set(statuses as string[])]
    if (known.length !== 1) return []
    return [{ id: g.id, label: g.items.length === 1 ? type.label : `${g.items.length} ${type.pluralLabel.toLowerCase()}`, status: known[0] as string, type, field }]
  })
  if (!chips.length) return null
  return (
    <ul className="flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <li key={c.id} className="inline-flex items-center gap-2 rounded-full border border-border py-0.5 ps-3 pe-1 text-xs text-muted-foreground">
          {c.label}
          <StatusPill value={c.status} field={c.field} type={c.type} className="h-5" />
        </li>
      ))}
    </ul>
  )
}

/** A reference shown as its record's title, with the key beside it; the key alone when no title is readable. */
export function ReferenceName({ scope, type, field, value, ontology }: { scope: Scope; type: TypeMeta; field: { link?: string }; value: unknown; ontology: Ontology | undefined }) {
  const t = useT()
  const target = type.links.find((l) => l.id === field.link)?.target
  const key = typeof value === "string" ? value : null
  const refs = useMemo(() => (target && key ? [{ objectType: target, key }] : []), [target, key])
  const index = useReferenceIndex(scope, refs)
  const titleOf = useTitleOf(ontology)
  if (!target || !key) return <span dir="ltr">{String(value ?? "—")}</span>
  const read = index(target, key)
  if (read.isError)
    return (
      <span className="block space-y-1">
        <span dir="ltr">{key}</span>
        <RefusalNote error={read.error} title={t("ws.related.unreadable")} className="px-2 py-1.5 text-xs" />
      </span>
    )
  const title = titleOf(read.data, key)
  return title.isKey ? (
    <span dir="ltr">{key}</span>
  ) : (
    <span className="inline-flex items-baseline gap-2">
      <span dir="auto">{title.title}</span>
      <span dir="ltr" className="text-xs text-subtle-foreground">
        {key}
      </span>
    </span>
  )
}
